import { Close, DeleteOutline, Done, Edit } from '@mui/icons-material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Stack,
  Chip,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  useTheme,
  ButtonBase,
  alpha,
  Box,
} from '@mui/material'

import React, { useEffect, useState } from 'react'

interface ConsolidatedBasketHeaderProps {
  basketHeaderName: string
  friendlyId: string
  variant?: 'active' | 'inactive'
  disableDelete?: boolean
  copyFriendlyId: () => void
  onDelete?: () => void
  onClose?: () => void
  onNameChange?: (newName: string) => void
}

const ConsolidatedBasketHeader: React.FC<ConsolidatedBasketHeaderProps> = ({
  basketHeaderName,
  friendlyId,
  variant = 'active',
  disableDelete,
  copyFriendlyId,
  onDelete,
  onClose,
  onNameChange,
}) => {
  const theme = useTheme()
  const [editValue, setEditValue] = useState(basketHeaderName)
  const [isEditingName, setIsEditingName] = useState(false)

  const handleNameChange = () => {
    if (!/^(?!\s*$).+/.test(editValue) || !onNameChange) return
    onNameChange(editValue)
    setIsEditingName(false)
  }

  useEffect(() => {
    setEditValue(basketHeaderName)
  }, [basketHeaderName])

  return (
    <Stack
      paddingX={2}
      paddingY={1}
      border={variant === 'active' ? 'none' : (theme) => theme.borders.light}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        backgroundColor:
          variant === 'active' ? theme.palette.primary.main : theme.palette.background.paper,
      }}>
      {onNameChange && isEditingName ? (
        <Box width="100%" bgcolor={alpha(theme.palette.primary.contrastText, 0.9)}>
          <TextField
            fullWidth
            variant="outlined"
            sx={{
              '.MuiInputBase-root': {
                fontSize: theme.typography.body2.fontSize,
                color: theme.palette.primary.main,
              },
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              handleNameChange()
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      data-cy="save-draft-order-name"
                      disabled={editValue === ''}
                      onClick={handleNameChange}>
                      <Done color="action" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            size="small"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
        </Box>
      ) : (
        <Stack direction="row" alignItems="center">
          <Typography
            variant="body2"
            color={variant === 'active' ? 'primary.contrastText' : 'text.secondary'}>
            {basketHeaderName}
            {onNameChange ? (
              <>
                &nbsp;
                <ButtonBase
                  data-cy="Edit-draft-order-name"
                  sx={{ scale: '0.9', position: 'relative', top: '-2px' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsEditingName(true)
                  }}>
                  <Edit htmlColor={theme.palette.primary.contrastText} fontSize="small" />
                </ButtonBase>
              </>
            ) : null}
          </Typography>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" spacing={1}>
        {friendlyId && !isEditingName && (
          <Chip
            size="small"
            sx={{
              // boxShadow:
              //   'inset 0 2px 4px rgba(0, 0, 0, 0.15), inset 0 -1px 2px rgba(255, 255, 255, 0.1)',
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              border: theme.borders.light,
              // '& .MuiChip-label': {
              //   color: theme.palette.text.primary,
              // },
            }}
            label={
              <Stack direction="row" alignItems="center">
                <IconButton
                  data-cy="ContentCopyIcon"
                  onClick={(e) => {
                    e.stopPropagation()
                    copyFriendlyId()
                  }}
                  size="small">
                  <ContentCopyIcon sx={{ transform: `scale(0.75)` }} />
                </IconButton>
                <Typography variant="caption" data-cy="consolidated-header-friendlyId">
                  {friendlyId}
                </Typography>
                {onDelete ? (
                  <IconButton
                    disabled={disableDelete}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete()
                    }}>
                    <DeleteOutline sx={{ transform: `scale(0.85)` }} />
                  </IconButton>
                ) : null}
              </Stack>
            }
          />
        )}
        {onClose ? (
          <IconButton onClick={() => onClose()}>
            <Close />
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  )
}

export default ConsolidatedBasketHeader
